import { PLAYER } from "../../../common/constants.ts";
import type { Player, TopProspects } from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";
import { league } from "../index.ts";
import fuzzRating from "./fuzzRating.ts";

// Who counts as a prospect, like MLB Pipeline: at most 25 years old and still rookie-eligible, meaning no more
// than 130 at bats and 50 innings (150 outs) in the majors over his career
export const MAX_PROSPECT_AGE = 25;
const ROOKIE_MAX_AB = 130;
const ROOKIE_MAX_OUTS = 150;
const rookieEligible = (p: Player) => {
	let ab = 0;
	let outs = 0;
	for (const row of p.stats ?? []) {
		if (!row.playoffs) {
			ab += row.ab ?? 0;
			outs += row.outs ?? 0;
		}
	}
	return ab <= ROOKIE_MAX_AB && outs <= ROOKIE_MAX_OUTS;
};

// Rank the scouting ratings the user can see, rather than revealing true ratings.
// Only signed minor leaguers belonging to an active organization are eligible.
export const rankTopProspects = (
	players: Player[],
	teams: { tid: number; abbrev: string; disabled?: boolean }[],
	season: number,
): TopProspects => {
	const organizations = new Map(
		teams.filter((t) => !t.disabled).map((t) => [t.tid, t.abbrev]),
	);
	const rows: TopProspects["players"] = [];
	for (const p of players) {
		const age = season - p.born.year;
		const r = p.ratings.at(-1);
		if (
			p.tid !== PLAYER.MINORS ||
			p.minorTid === undefined ||
			!organizations.has(p.minorTid) ||
			age < 0 ||
			age > MAX_PROSPECT_AGE ||
			!r ||
			!rookieEligible(p)
		) {
			continue;
		}
		rows.push({
			pid: p.pid,
			name: `${p.firstName} ${p.lastName}`,
			tid: p.minorTid,
			abbrev: organizations.get(p.minorTid)!,
			age,
			pos: r.pos,
			ovr: fuzzRating(r.ovr, r.fuzz),
			pot: fuzzRating(r.pot, r.fuzz),
		});
	}
	rows.sort(
		(a, b) => b.pot - a.pot || b.ovr - a.ovr || a.age - b.age || a.pid - b.pid,
	);
	return { season, players: rows.slice(0, 100) };
};

// One saved snapshot per year. The regular-season transition refreshes it after
// roster preparation. Older saves get their initial list on the first visit.
export const getTopProspects = async (
	refresh = false,
): Promise<TopProspects> => {
	const season = g.get("season");
	const saved = g.get("topProspects");
	if (!refresh && saved?.season === season) {
		return saved;
	}
	const snapshot = rankTopProspects(
		await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS),
		await idb.cache.teams.getAll(),
		season,
	);
	await league.setGameAttributes({ topProspects: snapshot });
	return snapshot;
};

// Farm system ranking: each organization scores 101 minus the rank of every prospect it has in the Top 100, so the
// best prospect is worth 100 points and the 100th is worth 1. Ties keep the order of the teams.
export const farmRanking = (
	ranking: TopProspects,
	teams: { tid: number; abbrev: string; disabled?: boolean }[],
) => {
	const rows = teams
		.filter((t) => !t.disabled)
		.map((t) => ({ tid: t.tid, abbrev: t.abbrev, points: 0, prospects: 0 }));
	const byTid = new Map(rows.map((row) => [row.tid, row]));
	ranking.players.forEach((p, i) => {
		const row = byTid.get(p.tid);
		if (row) {
			row.points += 100 - i;
			row.prospects += 1;
		}
	});
	rows.sort((a, b) => b.points - a.points || a.tid - b.tid);
	return rows;
};
